import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import ProductCard from '../components/shop/ProductCard';
import ProductModal from '../components/shop/ProductModal';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Footer from '../components/shared/Footer';
import Toast from '../components/shared/Toast';
import { useCart } from '../context/CartContext';
import { useAdmin } from '../context/AdminContext';
import { productsAPI, siteSettingsAPI } from '../services/api';
import { products as defaultProducts, categories as defaultCategories } from '../data/products';
import vagueHeroLogo from '../assets/images/vague-logo-new3.png';
import '../App.css';
import './Shop.css';

const isDiscountedProduct = (product) => {
  const discountedPrice = product.discountedPrice ?? product.discounted_price;
  return discountedPrice !== null
    && discountedPrice !== undefined
    && discountedPrice !== ''
    && Number(discountedPrice) < Number(product.price);
};

const Shop = () => {
  const { cart, addToCart, addTextureSetToCart, validateAndUpdateCart } = useCart();
  const { products: adminProducts, categories: adminCategories, textures: adminTextures, isInitialized: adminInitialized, refreshData } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [catalogueMode, setCatalogueMode] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('recommended');
  const [hotWindow, setHotWindow] = useState(30);
  const [hotProducts, setHotProducts] = useState([]);
  const [hotLoading, setHotLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedTexture, setSelectedTexture] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('warning');
  const [shopHeaderTitle, setShopHeaderTitle] = useState('VAGUE SHOP');
  const [shopHeaderSubtitle, setShopHeaderSubtitle] = useState('');
  const [shopHeaderBg, setShopHeaderBg] = useState('');

  useEffect(() => {
    siteSettingsAPI.get('shop_header_title,shop_header_subtitle,shop_header_bg_url')
      .then(res => {
        if (res.success && res.data) {
          if (res.data.shop_header_title) setShopHeaderTitle(res.data.shop_header_title);
          if (res.data.shop_header_subtitle !== undefined) setShopHeaderSubtitle(res.data.shop_header_subtitle);
          if (res.data.shop_header_bg_url) setShopHeaderBg(res.data.shop_header_bg_url);
        }
      })
      .catch(() => {});
  }, []);

  const products = adminInitialized && adminProducts.length > 0 ? adminProducts : defaultProducts;
  const categories = useMemo(() => {
    const source = adminInitialized && adminCategories.length > 0 ? adminCategories : defaultCategories;
    return source
      .map((category, index) => ({ category, index }))
      .sort((a, b) => {
        const aOrder = Number(a.category.displayOrder ?? a.category.display_order ?? a.index);
        const bOrder = Number(b.category.displayOrder ?? b.category.display_order ?? b.index);
        return aOrder - bOrder || a.category.name.localeCompare(b.category.name) || a.index - b.index;
      })
      .map(({ category }) => category);
  }, [adminInitialized, adminCategories]);
  const textures = adminInitialized && Array.isArray(adminTextures) && adminTextures.length > 0 ? adminTextures : [];

  useEffect(() => {
    if (catalogueMode !== 'hot') return;

    let active = true;
    setHotLoading(true);
    productsAPI.getHot(hotWindow)
      .then(response => {
        if (active) {
          setHotProducts(response.success && Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch(() => {
        if (active) setHotProducts([]);
      })
      .finally(() => {
        if (active) setHotLoading(false);
      });

    return () => {
      active = false;
    };
  }, [catalogueMode, hotWindow]);

  // Open modal from URL deep-link (e.g. /shop/42)
  useEffect(() => {
    if (!productId || !adminInitialized) return;
    const allProducts = adminProducts.length > 0 ? adminProducts : defaultProducts;
    const found = allProducts.find(p => String(p.id) === String(productId));
    if (found && !isModalOpen) {
      const defaultTex = found.textures && found.textures.length > 0 ? found.textures[0] : null;
      setSelectedProduct(found);
      setSelectedTexture(defaultTex);
      setIsModalOpen(true);
    }
  }, [productId, adminInitialized, adminProducts]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const validateCart = async () => {
      if (adminInitialized) {
        await refreshData();
      }

      setTimeout(() => {
        const currentProducts = adminInitialized && adminProducts.length > 0 ? adminProducts : defaultProducts;
        const currentTextures = adminInitialized && Array.isArray(adminTextures) && adminTextures.length > 0 ? adminTextures : [];
        if (cart.length > 0 && currentProducts.length > 0) {
          const hasChanges = validateAndUpdateCart(currentProducts, currentTextures);
          if (hasChanges) {
            setToastType('warning');
            setToastMessage('One or more items in your cart have been updated or removed due to product changes.');

            setTimeout(() => {
              setToastMessage(null);
            }, 5000);
          }
        }
      }, 100);
    };

    validateCart();
  }, [location.pathname]);

  const handleNavigateToBrand = (brand) => {
    window.scrollTo(0, 0);
    navigate('/', { state: { selectedBrand: brand } });
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.pageYOffset > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredProducts = useMemo(() => {
    const hotByProduct = new Map(hotProducts.map(item => [String(item.productId), item]));
    let filtered = products.map(product => {
      const hotPerformance = hotByProduct.get(String(product.id));
      return hotPerformance ? { ...product, hotRank: hotPerformance.rank, hotSalesQuantity: hotPerformance.quantity } : product;
    });

    if (catalogueMode === 'featured') {
      filtered = filtered.filter(product => product.featured === true);
    } else if (catalogueMode === 'new') {
      filtered = filtered.filter(product => product.newArrival === true);
    } else if (catalogueMode === 'hot') {
      filtered = filtered.filter(product => hotByProduct.has(String(product.id)));
    }

    if (selectedCategory === 'discounted') {
      filtered = filtered.filter(isDiscountedProduct);
    } else if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => {
        const primaryMatch = product.category === selectedCategory;
        const additionalMatch = product.categories && product.categories.includes(selectedCategory);
        return primaryMatch || additionalMatch;
      });
    }

    const normalizedSearch = searchQuery.trim().toLowerCase();
    if (normalizedSearch) {
      filtered = filtered.filter(product => [product.name, product.description, product.additionalInfo]
        .some(value => String(value || '').toLowerCase().includes(normalizedSearch)));
    }

    return [...filtered].sort((a, b) => {
      if (catalogueMode === 'hot' && a.hotRank !== b.hotRank) {
        return (a.hotRank || Number.MAX_SAFE_INTEGER) - (b.hotRank || Number.MAX_SAFE_INTEGER);
      }
      if (sortOrder === 'price-low') {
        return (parseFloat(a.discountedPrice ?? a.price) || 0) - (parseFloat(b.discountedPrice ?? b.price) || 0);
      }
      if (sortOrder === 'price-high') {
        return (parseFloat(b.discountedPrice ?? b.price) || 0) - (parseFloat(a.discountedPrice ?? a.price) || 0);
      }
      if (sortOrder === 'name') {
        return a.name.localeCompare(b.name);
      }
      if (sortOrder === 'newest') {
        const dateDifference = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        if (dateDifference !== 0) return dateDifference;
      }
      if (sortOrder === 'recommended') {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
      }
      return Number(a.display_order || 0) - Number(b.display_order || 0) || Number(a.id) - Number(b.id);
    });
  }, [products, selectedCategory, catalogueMode, searchQuery, sortOrder, hotProducts]);

  const categoryCounts = useMemo(() => {
    const counts = new Map();
    products.forEach(product => {
      const productCategories = new Set([product.category, ...(product.categories || [])]);
      productCategories.forEach(categoryId => {
        if (categoryId) counts.set(categoryId, (counts.get(categoryId) || 0) + 1);
      });
    });
    return counts;
  }, [products]);

  const handleProductClick = (product, texture) => {
    setSelectedProduct(product);
    setSelectedTexture(texture);
    setIsModalOpen(true);
    window.history.replaceState(null, '', `/shop/${product.id}`);
  };

  const handleAddToCart = (product, texture) => {
    try {
      addToCart(product, texture);
      setToastType('success');
      setToastMessage(`${product.name} added to cart!`);
      setTimeout(() => {
        setToastMessage(null);
      }, 3000);
    } catch (error) {
      setToastType('error');
      setToastMessage(error.message || 'Failed to add item to cart');
      setTimeout(() => {
        setToastMessage(null);
      }, 3000);
    }
  };

  const handleAddTextureSet = (product, selectedTextures) => {
    try {
      addTextureSetToCart(product, selectedTextures);
      setToastType('success');
      setToastMessage(`${product.name} texture set added to cart!`);
      setTimeout(() => setToastMessage(null), 3000);
    } catch (error) {
      setToastType('error');
      setToastMessage(error.message || 'Failed to add texture set');
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    window.history.replaceState(null, '', '/shop');
    setTimeout(() => {
      setSelectedProduct(null);
      setSelectedTexture(null);
    }, 300);
  };

  const menuItems = [
    {
      type: 'button',
      label: 'VELOURA',
      onClick: () => handleNavigateToBrand('veloura')
    },
    {
      type: 'link',
      label: 'SHOP',
      to: '/shop',
      active: true
    },
    {
      type: 'link',
      label: 'TEAM',
      to: '/team'
    },
    {
      type: 'link',
      label: 'CAREERS',
      to: '/careers'
    },
    {
      type: 'link',
      label: 'PARTNERS',
      to: '/partners'
    },
    {
      type: 'link',
      label: 'NEWS',
      to: '/news'
    },
    {
      type: 'link',
      label: 'ACCOUNT',
      to: '/account'
    }
  ];

  return (
    <div className="App">
      {}
      <ResponsiveNavbar
        menuItems={menuItems}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />

      {}
      <section
        className="shop-header"
        style={shopHeaderBg ? {
          backgroundImage: `url(${shopHeaderBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        } : undefined}
      >
        <div className="shop-header-content">
          <h1 className="shop-title shop-logo-title">
            <img
              src={vagueHeroLogo}
              alt={shopHeaderTitle || 'Vague'}
              className="shop-hero-logo"
            />
          </h1>
          {shopHeaderSubtitle && <p className="shop-subtitle">{shopHeaderSubtitle}</p>}
        </div>
      </section>
      {}
      <section className="shop-catalogue">
        <div className="shop-catalogue-container">
          <div className="shop-mode-bar" aria-label="Shop collections">
            {[
              { id: 'all', label: 'All' },
              { id: 'featured', label: 'Featured' },
              { id: 'hot', label: 'Hot' },
              { id: 'new', label: 'New' }
            ].map(mode => (
              <button
                key={mode.id}
                type="button"
                className={catalogueMode === mode.id ? 'active' : ''}
                onClick={() => setCatalogueMode(mode.id)}
              >
                {mode.label}
              </button>
            ))}
          </div>

          {catalogueMode === 'hot' && (
            <div className="hot-window-control" aria-label="Best-selling period">
              <span>Best sellers from</span>
              {[30, 60, 90].map(days => (
                <button
                  key={days}
                  type="button"
                  className={hotWindow === days ? 'active' : ''}
                  onClick={() => setHotWindow(days)}
                >
                  {days} days
                </button>
              ))}
            </div>
          )}

          <div className="shop-catalogue-layout">
            <aside className="shop-category-sidebar">
              <h2>Categories</h2>
              <button
                type="button"
                className={selectedCategory === 'all' ? 'active' : ''}
                onClick={() => setSelectedCategory('all')}
              >
                <span>All Products</span>
                <span>{products.length}</span>
              </button>
              <button
                type="button"
                className={selectedCategory === 'discounted' ? 'active' : ''}
                onClick={() => setSelectedCategory('discounted')}
              >
                <span>Discounted</span>
                <span>{products.filter(isDiscountedProduct).length}</span>
              </button>
              {categories.filter(category => !['all', 'limited'].includes(category.id)).map(category => (
                <button
                  key={category.id}
                  type="button"
                  className={selectedCategory === category.id ? 'active' : ''}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <span>{category.name}</span>
                  <span>{categoryCounts.get(category.id) || 0}</span>
                </button>
              ))}
            </aside>

            <div className="shop-results">
              <div className="shop-results-toolbar">
                <div>
                  <span className="shop-results-eyebrow">{catalogueMode === 'all' ? 'Shop' : catalogueMode}</span>
                  <h2>{filteredProducts.length} {filteredProducts.length === 1 ? 'Product' : 'Products'}</h2>
                </div>
                <div className="shop-results-controls">
                  <label className="shop-search-field">
                    <span className="sr-only">Search products</span>
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={event => setSearchQuery(event.target.value)}
                      placeholder="Search products"
                    />
                  </label>
                  <label className="shop-sort-field">
                    <span>Sort by</span>
                    <select value={sortOrder} onChange={event => setSortOrder(event.target.value)}>
                      <option value="recommended">Recommended</option>
                      <option value="newest">Newest</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="name">Name</option>
                    </select>
                  </label>
                </div>
              </div>

              {hotLoading ? (
                <div className="no-products"><p>Loading best sellers...</p></div>
              ) : filteredProducts.length > 0 ? (
                <div className="products-grid">
                  {filteredProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onProductClick={handleProductClick}
                      onAddToCart={handleAddToCart}
                    />
                  ))}
                </div>
              ) : (
                <div className="no-products">
                  <span />
                  <p>No products match these filters.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {}
      <ProductModal
        product={selectedProduct}
        selectedTexture={selectedTexture}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddToCart={handleAddToCart}
        onAddTextureSet={handleAddTextureSet}
      />
      {}
      <Footer />

      {}
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage(null)}
          duration={5000}
        />

      )}
    </div>

  );
};

export default Shop;
